import React, { useState, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import axios from 'axios';

const inputTypes = ['text', 'email', 'number', 'select', 'checkbox', 'file'];

type FieldType = {
  id: number;
  label: string;
  type: string;
  options?: string[];
  required: boolean;
  isNew?: boolean;
};

const Eventregisterform = () => {
  const api = process.env.REACT_APP_API_URL as string;
  const [fields, setFields] = useState<FieldType[]>([]);
  const [qrImage, setQrImage] = useState<File | null>(null);
  const [qrPreview, setQrPreview] = useState<string | null>(null);

  useEffect(() => {
  const fetchFields = async () => {
    try {
      const res = await axios.get(`${api}/auth/eventformregister`);
      const fetchedFields = res.data.data || [];

      const processedFields = fetchedFields.map((field: FieldType) => {
        // Convert options from string to array if needed
        const isOptionType = field.type === 'select' || field.type === 'checkbox';
        const optionsArray =
          isOptionType && typeof field.options === 'string'
            ? (field.options as string).split(',').map((opt) => opt.trim())
            : field.options;

        return {
          ...field,
          options: optionsArray || [],
          isNew: false,
        };
      });

      setFields(processedFields);

      if (res.data.qrImage) {
        setQrPreview(`${api}/uploads/${res.data.qrImage}`);
      }
    } catch (err) {
      console.error('Error fetching fields:', err);
    }
  };

  fetchFields();
}, [api]);


  const addField = () => {
    const newField: FieldType = {
      id: Date.now(),
      label: 'New Field',
      type: 'text',
      options: [],
      required: false,
      isNew: true,
    };
    setFields([...fields, newField]);
  };

  const updateField = (id: number, key: keyof FieldType, value: any) => {
    setFields(fields.map((f) => (f.id === id ? { ...f, [key]: value } : f)));
  };

  const removeField = async (id: number) => {
    const field = fields.find((f) => f.id === id);
    if (!field) return;

    if (!window.confirm('Are you sure you want to delete this field?')) return;

    if (!field.isNew) {
      try {
        await axios.delete(`${api}/auth/eventformregister/${id}`);
      } catch (err) {
        console.error('Error deleting field:', err);
        alert('Error deleting field');
        return;
      }
    }

    setFields(fields.filter((f) => f.id !== id));
  };

  const onDragEnd = (result: any) => {
    if (!result.destination) return;
    const reordered = [...fields];
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);
    setFields(reordered);
  };

  const renderInput = (field: FieldType) => {
    switch (field.type) {
      case 'select':
      case 'checkbox':
        return (
          <div className="mb-4">
            <label className="block text-sm mb-1">Options (comma separated)</label>
            <input
              type="text"
              placeholder="Option1,Option2"
            value={Array.isArray(field.options) ? field.options.join(',') : ''}
              onChange={(e) =>
                updateField(field.id, 'options', e.target.value.split(',').map((opt) => opt.trim()))
              }
              className="text-black px-3 py-2 rounded w-full"
            />
          </div>
        );
      case 'file':
        return (
          <div className="mb-4">
            <label className="block text-sm mb-1">File Upload Preview</label>
            <input
              type="file"
              disabled
              className="bg-gray-100 text-gray-500 px-3 py-2 rounded w-full cursor-not-allowed"
            />
          </div>
        );
      default:
        return (
          <div className="mb-4">
            <label className="block text-sm mb-1">Preview</label>
            <input
              type={field.type}
              disabled
              placeholder={`Enter your ${field.type}`}
              className="bg-gray-100 text-gray-500 px-3 py-2 rounded w-full"
            />
          </div>
        );
    }
  };

  const handleSave = async () => {
    const formData = new FormData();
    if (qrImage) formData.append('qrImage', qrImage);

    const newFields = fields.filter((f) => f.isNew);
    if (newFields.length === 0 && !qrImage) {
      alert("No new fields or QR code to save.");
      return;
    }

    formData.append('fields', JSON.stringify(newFields));

    try {
      await axios.post(`${api}/auth/eventformregister`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      // Reset new field markers after save
      setFields((prev) =>
        prev.map((field) => ({ ...field, isNew: false }))
      );
      setQrImage(null); // optional
      alert('Form saved!');
    } catch (err) {
      console.error('Save error:', err);
      alert('Error saving form');
    }
  };

  return (
    <div className="bg-[#0F172A] min-h-screen py-10 px-4 md:px-10">
      <div className="max-w-4xl mx-auto bg-[#1E293B] rounded-xl shadow-lg p-6">
        <h2 className="text-3xl font-bold text-white mb-6 border-b border-[#334155] pb-4">
          📝 Event Registration Form Builder
        </h2>

        {/* QR Upload */}
        <div className="mb-8">
          <h3 className="text-xl font-semibold text-white mb-3">📎 QR Code Upload</h3>
          {qrImage ? (
            <img
              src={URL.createObjectURL(qrImage)}
              alt="QR"
              className="w-32 h-32 object-contain border rounded mb-2"
            />
          ) : qrPreview ? (
            <img
              src={qrPreview}
              alt="QR"
              className="w-32 h-32 object-contain border rounded mb-2"
            />
          ) : (
            <p className="text-gray-300 mb-2">No QR code uploaded yet.</p>
          )}
          <input
            type="file"
            accept="image/*"
            onChange={(e) => {
              if (e.target.files?.[0]) setQrImage(e.target.files[0]);
            }}
            className="bg-white text-black px-3 py-2 rounded w-full"
          />
        </div>

        <div className="flex justify-end mb-6">
          <button
            onClick={addField}
            className="bg-yellow-400 hover:bg-yellow-500 text-black font-semibold px-5 py-2 rounded-md shadow-md"
          >
            ➕ Add Field
          </button>
        </div>

        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="fields">
            {(provided) => (
              <div ref={provided.innerRef} {...provided.droppableProps} className="space-y-6">
                {fields.map((field, index) => (
                  <Draggable key={field.id.toString()} draggableId={field.id.toString()} index={index}>
                    {(provided) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        className="bg-[#334155] rounded-lg p-6 text-white relative shadow-md"
                      >
                        <input
                          className="text-black px-3 py-2 rounded w-full mb-3"
                          type="text"
                          value={field.label}
                          onChange={(e) => updateField(field.id, 'label', e.target.value)}
                          placeholder="Enter field label"
                        />

                        <select
                          value={field.type}
                          onChange={(e) => updateField(field.id, 'type', e.target.value)}
                          className="text-black px-3 py-2 rounded w-full mb-3"
                        >
                          {inputTypes.map((type) => (
                            <option key={type} value={type}>{type}</option>
                          ))}
                        </select>

                        {renderInput(field)}

                        <label className="flex items-center mt-3">
                          <input
                            type="checkbox"
                            checked={field.required}
                            onChange={(e) =>
                              updateField(field.id, 'required', e.target.checked)
                            }
                            className="mr-2 accent-yellow-400"
                          />
                          Required
                        </label>

                        <button
                          onClick={() => removeField(field.id)}
                          className="absolute top-4 right-4 bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded text-sm"
                        >
                          🗑 Remove
                        </button>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>

        <div className="text-right mt-10">
          <button
            onClick={handleSave}
            className="bg-green-500 hover:bg-green-600 text-white font-medium px-6 py-2 rounded-lg shadow"
          >
            💾 Save to Database
          </button>
        </div>
      </div>
    </div>
  );
};

export default Eventregisterform;
